import { supabase } from '../../lib/supabase';
import { Stat, CreateStatDTO } from '../../types';

export const statService = {
  async getAllByUserId(userId: string): Promise<Stat[]> {
    const { data, error } = await supabase
      .from('stats')
      .select('*')
      .eq('user_id', userId)
      .order('date_recorded', { ascending: false });

    if (error) throw error;
    return (data || []) as Stat[];
  },

  async create(stat: CreateStatDTO): Promise<Stat> {
    const { data, error } = await supabase.from('stats').insert([stat]).select().single();
    if (error) throw error;
    return data as Stat;
  },

  async delete(id: string): Promise<boolean> {
    const { error } = await supabase.from('stats').delete().eq('id', id);
    if (error) throw error;
    return true;
  },

  async getRankings(
    metricName: string
  ): Promise<{ id: string; name: string; school: string; value: string; rank: number }[]> {
    // Step 1: Fetch verified stats for this metric (no join — FK may not exist yet)
    const { data: statsData, error: statsError } = await supabase
      .from('stats')
      .select('id, value, user_id')
      .eq('name', metricName)
      .eq('verification_status', 'verified');

    if (statsError) throw statsError;
    if (!statsData || statsData.length === 0) return [];

    // Step 2: Fetch the profiles for those user_ids
    const userIds = [...new Set(statsData.map((s: any) => s.user_id))];
    const { data: profilesData } = await supabase
      .from('profiles')
      .select('id, full_name, school')
      .in('id', userIds);

    const profileMap: Record<string, { full_name: string; school: string }> = {};
    (profilesData || []).forEach((p: any) => {
      profileMap[p.id] = { full_name: p.full_name, school: p.school };
    });

    // Step 3: Sort and rank
    return statsData
      .map((s: any) => ({
        id: s.id,
        name: profileMap[s.user_id]?.full_name || 'Anonymous',
        school: profileMap[s.user_id]?.school || 'Unknown',
        value: s.value,
        numericValue: parseFloat(s.value.replace(/[^0-9.]/g, '')) || 0,
      }))
      .sort((a, b) => {
        const isTime =
          metricName.toLowerCase().includes('dash') ||
          metricName.toLowerCase().includes('drill') ||
          metricName.toLowerCase().includes('sprint');
        return isTime ? a.numericValue - b.numericValue : b.numericValue - a.numericValue;
      })
      .map((s, i) => ({ id: s.id, name: s.name, school: s.school, value: s.value, rank: i + 1 }));
  },

  async getFeed(): Promise<
    {
      id: string;
      user: { full_name: string; avatar_url: string; school: string };
      content: string;
      timestamp: string;
      likes: number;
      comments: number;
    }[]
  > {
    // Step 1: Fetch recent verified stats (no join)
    const { data: statsData, error: statsError } = await supabase
      .from('stats')
      .select('id, name, value, created_at, user_id')
      .eq('verification_status', 'verified')
      .order('created_at', { ascending: false })
      .limit(20);

    if (statsError) throw statsError;
    if (!statsData || statsData.length === 0) return [];

    // Step 2: Fetch profiles for those users
    const userIds = [...new Set(statsData.map((s: any) => s.user_id))];
    const { data: profilesData } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url, school')
      .in('id', userIds);

    const profileMap: Record<string, { full_name: string; avatar_url: string; school: string }> =
      {};
    (profilesData || []).forEach((p: any) => {
      profileMap[p.id] = {
        full_name: p.full_name,
        avatar_url: p.avatar_url,
        school: p.school,
      };
    });

    // Step 3: Assemble feed items
    return statsData.map((s: any) => {
      const profile = profileMap[s.user_id];
      return {
        id: s.id,
        user: {
          full_name: profile?.full_name || 'Anonymous',
          avatar_url:
            profile?.avatar_url ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(profile?.full_name || 'A')}&background=0D8ABC&color=fff`,
          school: profile?.school || 'Unknown',
        },
        content: `Logged a new verified PR: ${s.name} – ${s.value}`,
        timestamp: new Date(s.created_at).toLocaleDateString(),
        likes: 0,
        comments: 0,
      };
    });
  },
};
